import React, { useCallback, useEffect, useRef } from "react";
import "../Finance.css";
import "./Withdraw.css";
import { FiArrowUpRight, FiCheckCircle, FiClock, FiInfo, FiShield } from "react-icons/fi";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useGlobalContext } from "../../../context/context";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import OTPModal from "../Modal";
import SelectPaymentMethod from "./Select-Payout-Method";
import BankTransfer from "./BankTransfer";
import CryptoTransfer from "./CryptoTransfer";
import { getTransferSchema, sendOtherdataStill } from "./helperMethod";
import { yupResolver } from "@hookform/resolvers/yup";
import { getWalletInvestmentPortfolio } from "../../../utils/investmentApi";

const walletAmountFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function Withdraw() {
  const [transactionType, setTransactionType] = useState("");
  const [otp, setOtp] = useState(0);
  const [loading, setLoading] = useState(false);
  const [walletPortfolio, setWalletPortfolio] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState("");
  const balanceRequestRef = useRef(0);

  const {
    handleSubmit,
    register,
    formState: { isValid, errors },
  } = useForm({ resolver: yupResolver(getTransferSchema(transactionType)) });

  const onError = () => {
    if (!isValid) {
      Object.values(errors).forEach((err) => {
        toast.error(err.message || "All inputs field must be valid");
      });
    }
  };
  const { userDetails, getUser, baseUrl } = useGlobalContext();
  const accessToken = JSON.parse(sessionStorage.getItem("accessToken"));
  const navigate = useNavigate();
  const {
    firstName,
    transferStep,
    status,
    country,
    symbol,
    withdrawalText,
  } = userDetails;
  const genSymbol = symbol === "" ? country?.symbol : symbol;

  const refreshWalletBalance = useCallback(async (signal) => {
    const requestId = ++balanceRequestRef.current;
    setBalanceLoading(true);
    setBalanceError("");
    try {
      const response = await getWalletInvestmentPortfolio(baseUrl, { signal });
      const portfolio = response && response.portfolio;
      const balance = Number(portfolio && (portfolio.availableBalance ?? portfolio.cash));
      if (!Number.isFinite(balance)) {
        throw new Error("The investment service returned an invalid wallet balance.");
      }
      if (signal?.aborted || requestId !== balanceRequestRef.current) return null;
      const nextPortfolio = {
        availableBalance: balance,
        currencyCode: String(portfolio.currencyCode || "").trim(),
        currencySymbol: String(portfolio.currencySymbol || "").trim(),
      };
      setWalletPortfolio(nextPortfolio);
      return balance;
    } catch (error) {
      if (signal?.aborted || requestId !== balanceRequestRef.current) return null;
      setWalletPortfolio(null);
      setBalanceError(error.message || "Your available wallet balance could not be loaded.");
      return null;
    } finally {
      if (!signal?.aborted && requestId === balanceRequestRef.current) setBalanceLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    const controller = new AbortController();
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshWalletBalance(controller.signal);
    };
    refreshWalletBalance(controller.signal);
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      controller.abort();
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refreshWalletBalance]);

  const formattedAvailableBalance = walletPortfolio
    ? (walletPortfolio.currencySymbol || (walletPortfolio.currencyCode ? walletPortfolio.currencyCode + " " : "")) +
      walletAmountFormat.format(walletPortfolio.availableBalance)
    : "";

  const handleSteps = async (data) => {
    if (loading) return;
    setLoading(true);
    getUser(accessToken);
    const amount = Number(data.amount);
    const availableBalance = await refreshWalletBalance();

    if (availableBalance === null) {
      toast.error("Could not verify your available balance. Please retry.");
      setLoading(false);
      return;
    }
    if (status === "false") {
      toast.error(withdrawalText);
      setLoading(false);
      setTimeout(() => navigate("/wallet"), 3000);
      return;
    }
    if (!Number.isFinite(amount)) {
      toast.error("Enter a valid withdrawal amount.");
      setLoading(false);
      return;
    }
    if (amount < 500) {
      toast.error("Withdrawal amount must be at least 500" + genSymbol);
      setLoading(false);
      return;
    }
    if (amount > availableBalance + 0.001) {
      toast.error("Insufficient available wallet balance.");
      setLoading(false);
      return;
    }
    if (!isValid) {
      setLoading(false);
      return;
    }

    if (transferStep !== 13) {
      toast.success("Withdrawal verification is in progress.");
      setTimeout(() => setOtp(1), 2000);
      setLoading(false);
      return;
    }

    setOtp(0);
    const transferData = { ...data, userId: userDetails._id };
    sendOtherdataStill(transactionType, transferData);
    try {
      await axios.post(baseUrl + "transfer", transferData, {
        headers: { token: accessToken },
      });
      toast.success("Withdrawal request submitted. Funds are reserved pending approval.");
      refreshWalletBalance();
      setTimeout(() => navigate("/transactions"), 2000);
    } catch (error) {
      toast.error(error.response?.data?.message || "Withdrawal failed. Please try again.");
      refreshWalletBalance();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUser(accessToken);
    if (transferStep === 2) {
      navigate("/account");
    } else if (transferStep === 4) {
      navigate("/account");
    } else if (transferStep === 6) {
      navigate("/account");
    } else if (transferStep === 8) {
      navigate("/account");
    } else if (transferStep === 10) {
      navigate("/account");
    } else if (transferStep === 12) {
      navigate("/account");
    }
  }, []);

  return (
    <section className="pg-finance-page pg-withdraw-page">
      <div className="pg-finance-topbar"><DashboardHeader pathname={firstName} /></div>
      <main className="pg-finance-content">
        <header className="pg-finance-title"><div><span className="pg-finance-eyebrow">ACCOUNT SERVICES / WITHDRAWAL</span><h1>Withdraw funds</h1><p>Choose where to send your funds, then review your request details.</p></div><span className="pg-finance-badge"><FiArrowUpRight size={14} /> WITHDRAWAL</span></header>
        <section className="pg-withdraw-balance" role={balanceError ? "alert" : "status"} aria-live="polite">
          <div className="pg-withdraw-balance-copy"><span className="pg-finance-eyebrow">AVAILABLE TO WITHDRAW</span><p>Current balance from your wallet</p></div>
          {balanceLoading ? <span className="pg-withdraw-balance-value">Checking current balance…</span> : balanceError ? <div className="pg-withdraw-balance-error"><span>{balanceError}</span><button type="button" onClick={() => refreshWalletBalance()}>Retry</button></div> : <strong className="pg-withdraw-balance-value">{formattedAvailableBalance}</strong>}
        </section>
        <div className="pg-finance-layout pg-withdraw-layout">
          <section className="pg-finance-panel pg-withdraw-panel" aria-label="Withdrawal request">
            <div className="pg-finance-progress" aria-label="Withdrawal steps"><div className={"pg-finance-progress-step " + (transactionType ? "is-complete" : "is-current")}><span className="pg-finance-progress-number">{transactionType ? <FiCheckCircle size={13} /> : "1"}</span><span>Choose method</span></div><div className={"pg-finance-progress-step " + (transactionType ? "is-current" : "")}><span className="pg-finance-progress-number">2</span><span>Enter details</span></div></div>
            {transactionType === "" ? <SelectPaymentMethod setTransactionType={setTransactionType} /> : transactionType === "Bank Transfer" ? <BankTransfer register={register} genSymbol={genSymbol} handleSteps={handleSteps} handleSubmit={handleSubmit} loading={loading} onError={onError} onBack={() => setTransactionType("")} /> : <CryptoTransfer register={register} genSymbol={genSymbol} handleSteps={handleSteps} handleSubmit={handleSubmit} loading={loading} onError={onError} onBack={() => setTransactionType("")} />}
          </section>
          <aside className="pg-finance-aside pg-withdraw-aside" aria-labelledby="withdraw-help-title">
            <span className="pg-finance-icon"><FiShield size={19} /></span><span className="pg-finance-eyebrow">BEFORE YOU SUBMIT</span><h2 id="withdraw-help-title">Check the details carefully</h2><p>Review your destination account or wallet address before submitting. Withdrawal requests may require verification and approval.</p>
            <div className="pg-withdraw-minimum"><span>Minimum request</span><strong>{genSymbol || ""}500</strong></div>
            <ul className="pg-finance-aside-list"><li><FiCheckCircle size={14} /><span><strong>Confirm the destination</strong>Make sure the bank details or network match.</span></li><li><FiClock size={14} /><span><strong>Follow request status</strong>Check Transactions for review updates.</span></li></ul>
            <div className="pg-finance-note"><FiInfo size={14} /><span>Only submit a request you recognize. Contact support if a status or balance looks wrong.</span></div>
          </aside>
        </div>
        <footer className="pg-finance-footer"><span>ProfitGrowing</span><span>Account services</span></footer>
      </main>
      {otp === 1 ? <OTPModal /> : null}<ToastContainer />
    </section>
  );
}
export default Withdraw;
